import MyFirstGameTooltipArrow from '../../../assets/MyFirstGame/MyFirstGameTooltipArrow.svg'
import MyFirstGameTooltipArrowLeft from '../../../assets/MyFirstGame/MyFirstGameTooltipArrowLeft.svg'
import MyFirstGameTooltipArrowTop from '../../../assets/MyFirstGame/MyFirstGameTooltipArrowTop.svg'
import { DS } from '../../design-system'
import { LinearGradient } from 'expo-linear-gradient'
import {
  Animated as RNAnimated,
  TouchableOpacity,
  ViewStyle,
  StyleSheet,
  Platform,
  View,
  useWindowDimensions
} from 'react-native'
import { useStore } from '../../models'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { useResponsive } from '@/screens/new-design/hooks'
import { Typography } from '@/design-system/Typography'

const isWeb = Platform.OS === 'web'

const MyFirstGameSingleTooltip = observer(
  ({
    title,
    description,
    showNext = true,
    showArrow = false,
    arrowPosition = 'bottom',
    position = 'bottom',
    wrapContent = false,
    style = {},
    containerStyle = {}
  }: {
    title: string
    description?: string
    showNext?: boolean
    showArrow?: boolean
    position?: 'bottom' | 'top'
    arrowPosition?: 'bottom' | 'topRight' | 'topLeft' | 'bottomLeft' | 'left'
    wrapContent?: boolean
    style?: ViewStyle
    containerStyle?: ViewStyle
  }) => {
    const {
      game: { goToNextFirstGameStep }
    } = useStore()

    const { verticalScale } = useResponsive()

    const opacity = useRef(new RNAnimated.Value(0)).current

    const { width } = useWindowDimensions()
    const isSmallDevice = width < 380

    useEffect(() => {
      RNAnimated.timing(opacity, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true
      }).start()
    }, [opacity])

    const renderArrow = () => {
      if (arrowPosition === 'bottom') {
        return (
          <MyFirstGameTooltipArrow
            preserveAspectRatio="xMidYMid meet"
            // @ts-expect-error: web-only value
            style={isWeb ? webStyles.bottomArrow : styles.bottomArrow}
          />
        )
      }

      if (arrowPosition === 'topRight') {
        return (
          <MyFirstGameTooltipArrowTop
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              top: -14,
              right: 38
            }}
          />
        )
      }

      if (arrowPosition === 'topLeft') {
        return (
          <MyFirstGameTooltipArrowTop
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              top: -14,
              left: 24
            }}
          />
        )
      }

      if (arrowPosition === 'bottomLeft') {
        return (
          <MyFirstGameTooltipArrow
            preserveAspectRatio="xMidYMid meet"
            // @ts-expect-error: web-only value
            style={isWeb ? webStyles.bottomArrow : [styles.bottomArrow, { left: 16 }]}
          />
        )
      }

      if (arrowPosition === 'left') {
        return (
          <MyFirstGameTooltipArrowLeft
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              left: -8
            }}
          />
        )
      }
    }

    return (
      <RNAnimated.View style={[{ flex: 1, zIndex: 99 }, { opacity }]}>
        <DS.Frame
          style={[
            {
              left: wrapContent ? undefined : 32,
              right: wrapContent ? undefined : 32,
              bottom: position === 'bottom' ? (isSmallDevice ? 80 : 88) : undefined,
              // bottom: position === 'bottom' ? 20 : undefined,
              top: position === 'top' ? 24 : undefined,
              borderRadius: wrapContent ? 24 : 16
            },
            styles.container,
            // @ts-expect-error: web-only value
            isWeb ? webStyles.container : {},
            {
              zIndex: 99,
              flexShrink: 1
            },
            containerStyle
          ]}
        >
          <LinearGradient
            colors={['#6713D3', '#480D93']}
            locations={[0, 1]}
            style={[{ borderRadius: wrapContent ? 24 : 16 }, styles.gradient]}
          />
          {showArrow && renderArrow()}
          <DS.Frame
            style={
              !isWeb
                ? [{ flex: wrapContent ? undefined : 1, gap: 4 }, style]
                : [
                    {
                      flexShrink: 1
                    },
                    style
                  ]
            }

            // then, before passing to Frame, you can remove keys with null values if you want
          >
            <DS.Text
              variant={'Body Large/Medium'}
              color={'Global/White'}
              style={{
                fontSize: verticalScale(Number(Typography['Body Large/Medium'].fontSize)),
                lineHeight: verticalScale(Number(Typography['Body Large/Medium'].lineHeight))
              }}
            >
              {title}
            </DS.Text>
            {description && (
              <DS.Text
                variant={'Body Medium/Regular'}
                color={'Global/White'}
                style={{
                  fontSize: verticalScale(Number(Typography['Body Medium/Regular'].fontSize)),
                  lineHeight: verticalScale(Number(Typography['Body Medium/Regular'].lineHeight))
                }}
              >
                {description}
              </DS.Text>
            )}
          </DS.Frame>
          {showNext && (
            <TouchableOpacity style={styles.arrow} onPress={goToNextFirstGameStep}>
              <DS.Icon name={'Arrow---right'} size={24} color={'Global/Black'} />
            </TouchableOpacity>
          )}
        </DS.Frame>
      </RNAnimated.View>
    )
  }
)
const webStyles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 0,
    right: 0,
    // bottom: 88,
    marginInline: 'auto',
    // @ts-expect-error: web-only value
    width: 'fit-content',
    // @ts-expect-error: web-only value
    height: 'fit-content'
  },
  bottomArrow: {
    position: 'absolute',
    bottom: -13,
    //left: 16,
    backgroundColor: '#6713D300'
  }
})

const styles = StyleSheet.create({
  arrow: {
    marginLeft: 12,
    width: 48,
    height: 48,
    padding: 12,
    gap: 8,
    backgroundColor: 'white',
    borderRadius: 96
  },
  gradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderWidth: 2,
    borderColor: '#822852'
  },
  container: {
    position: 'absolute',
    zIndex: 99,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#6713D3',
    shadowColor: 'black',
    elevation: 10,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 3
  },
  bottomArrow: {
    position: 'absolute',
    bottom: -13,
    shadowColor: 'black',
    backgroundColor: '#6713D300',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 3
  }
})

export default MyFirstGameSingleTooltip

export const ToolTip = observer(
  ({
    title,
    description,
    showNext = true,
    showArrow = false,
    arrowPosition = 'bottom',
    contentStyle = {},
    containerStyle = {}
  }: {
    title: string
    description?: string
    showNext?: boolean
    showArrow?: boolean
    arrowPosition?: 'bottom' | 'topRight' | 'topLeft' | 'bottomLeft' | 'left'
    contentStyle?: ViewStyle
    containerStyle?: ViewStyle
  }) => {
    const {
      game: { goToNextFirstGameStep }
    } = useStore()

    const { verticalScale } = useResponsive()

    const renderArrow = () => {
      if (arrowPosition === 'bottom') {
        return (
          <MyFirstGameTooltipArrow
            preserveAspectRatio="xMidYMid meet"
            style={{
              alignSelf: 'center',
              position: 'absolute',
              bottom: -16,
              zIndex: 3
            }}
          />
        )
      }

      if (arrowPosition === 'topRight') {
        return (
          <MyFirstGameTooltipArrowTop
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              top: -16,
              right: 16
            }}
          />
        )
      }

      if (arrowPosition === 'topLeft') {
        return (
          <MyFirstGameTooltipArrowTop
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              top: -16,
              left: 16
            }}
          />
        )
      }

      if (arrowPosition === 'bottomLeft') {
        return (
          <MyFirstGameTooltipArrow
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              bottom: -16,
              left: 16
            }}
          />
        )
      }

      if (arrowPosition === 'left') {
        return (
          <MyFirstGameTooltipArrowLeft
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              zIndex: 0,
              top: 6,
              left: -12
            }}
          />
        )
      }
    }
    return (
      <View
        style={[
          {
            zIndex: 99,

            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#6713D3',
            borderWidth: 2,
            borderColor: '#822852',
            borderRadius: 24,

            paddingHorizontal: 16,
            paddingVertical: 12,

            shadowColor: 'black',
            elevation: 10,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.5,
            shadowRadius: 3
          },
          containerStyle
        ]}
      >
        <LinearGradient
          colors={['#6713D3', '#480D93']}
          locations={[0, 1]}
          style={[
            StyleSheet.absoluteFillObject,
            {
              zIndex: 0,
              borderRadius: 24
            }
          ]}
        />
        <View
          style={[
            {
              gap: 4,
              maxWidth: '100%',
              flexShrink: 1
            },
            contentStyle
          ]}
        >
          <DS.Text
            variant={'Body Large/Medium'}
            color={'Global/White'}
            // style={{
            //   fontSize: verticalScale(Number(Typography['Body Large/Medium'].fontSize)),
            //   lineHeight: verticalScale(Number(Typography['Body Large/Medium'].lineHeight))
            // }}
          >
            {title}
          </DS.Text>

          {description && (
            <DS.Text
              variant={'Body Medium/Regular'}
              color={'Global/White'}
              // style={{
              //   fontSize: verticalScale(Number(Typography['Body Medium/Regular'].fontSize)),
              //   lineHeight: verticalScale(Number(Typography['Body Medium/Regular'].lineHeight))
              // }}
            >
              {description}
            </DS.Text>
          )}
        </View>

        {showNext && (
          <TouchableOpacity
            style={{
              marginLeft: 12,
              width: 48,
              height: 48,
              padding: 12,
              gap: 8,
              backgroundColor: 'white',
              borderRadius: 96
            }}
            onPress={goToNextFirstGameStep}
          >
            <DS.Icon name={'Arrow---right'} size={24} color={'Global/Black'} />
          </TouchableOpacity>
        )}
        {showArrow && renderArrow()}
      </View>
    )
  }
)
